import { Card, CardContent } from "@/components/ui/card";
import { Building2, CheckCircle2, XCircle, Users, Crown, Calendar, CreditCard } from "lucide-react";
import { Company } from "@/types/company";
import { cn } from "@/lib/utils";

interface CompanyStatsProps {
  companies: Company[];
}

export function CompanyStats({ companies }: CompanyStatsProps) {
  const stats = {
    total: companies.length,
    active: companies.filter((c) => c.license.isActive).length,
    inactive: companies.filter((c) => !c.license.isActive).length,
    totalAdmins: companies.reduce((acc, c) => acc + (c.admins?.length || 0), 0),
    premium: companies.filter((c) => c.license.type === 'premium').length,
    trial: companies.filter((c) => c.license.type === 'trial').length,
    totalLicenseCount: companies.reduce((acc, c) => acc + (c.license.count || 0), 0),
    expiringSoon: companies.filter((c) => {
      const expiryDate = new Date(c.license.expiryDate);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays > 0 && c.license.isActive;
    }).length
  };

  const items = [
    {
      label: "Toplam Firma",
      value: stats.total,
      icon: Building2,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Aktif Lisans",
      value: stats.active,
      icon: CheckCircle2,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Pasif Lisans",
      value: stats.inactive,
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
    },
    {
      label: "Premium Firmalar",
      value: stats.premium,
      icon: Crown,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    {
      label: "Trial Firmalar",
      value: stats.trial,
      icon: Calendar,
      color: "text-blue-600",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Toplam Lisans Adedi",
      value: stats.totalLicenseCount,
      icon: CreditCard,
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
    },
    {
      label: "30 Gün İçinde Bitecek",
      value: stats.expiringSoon,
      icon: Calendar,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
    {
      label: "Toplam Yönetici",
      value: stats.totalAdmins,
      icon: Users,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              {/* Arka plan dekoratif gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>

                {/* Alt bilgi çubuğu */}
                <div className="pt-3 border-t border-slate-100">
                  {item.label === "Aktif Lisans" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-green-500 animate-pulse" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} aktif
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-green-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Premium Firmalar" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-amber-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} premium
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-amber-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "30 Gün İçinde Bitecek" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-orange-500 animate-pulse" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          {item.value > 0 ? "Acil takip gerekli" : "Takip gerektirmiyor"}
                        </span>
                      </div>
                      {item.value > 0 && <span className="text-xs font-semibold text-orange-600">{item.value}</span>}
                    </div>
                  )}

                  {item.label === "Pasif Lisans" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-red-500" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} pasif
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-red-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Trial Firmalar" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? "bg-blue-600" : "bg-slate-300"}`}></span>
                        <span className="font-medium">
                          %{stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} trial
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-blue-600">{item.value}/{stats.total}</span>
                    </div>
                  )}

                  {item.label === "Toplam Firma" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-blue-500"></span>
                        <span className="font-medium">Sistemde kayıtlı</span>
                      </div>
                      <span className="text-xs font-semibold text-blue-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Toplam Lisans Adedi" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-indigo-500"></span>
                        <span className="font-medium">Toplam cihaz kapasitesi</span>
                      </div>
                      <span className="text-xs font-semibold text-indigo-600">{item.value}</span>
                    </div>
                  )}

                  {item.label === "Toplam Yönetici" && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className="inline-block w-2 h-2 rounded-full mr-2 bg-purple-500"></span>
                        <span className="font-medium">Aktif kullanıcı</span>
                      </div>
                      <span className="text-xs font-semibold text-purple-600">{item.value}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
